// Fin de vie d'un dossier : menu « Plus » de l'en-tête (classer sans suite, rouvrir, effacer définitivement),
// bandeau « lecture seule » d'un dossier classé, dialogues de classement et d'effacement.
// Le serveur fait foi (droits, lecture seule, refus) ; l'interface n'affiche que les actions permises.
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, MoreHorizontal, Trash2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, pluriel } from '../../lib/format.ts';
import { Alerte, Bouton, Chargement, ChampChoix, ChampTexte, Dialogue, EtatErreur, useNotifier } from '../../components/ui.tsx';
import { COMMENTAIRE_MAX, LIBELLES_MOTIF, MOTIFS_CLASSEMENT, confirmationValide, type MotifClassement } from '@shared/classement.ts';

export interface ClassementDossier { le: string; par: string | null; motif: MotifClassement; commentaire: string | null }
interface DossierMin { id: string; reference: string; title: string; classement?: ClassementDossier | null }

interface Recapitulatif {
  id: string; reference: string; title: string; demo: boolean;
  comptes: Record<string, number>;
  refus: { code: string; message: string } | null;
  clientEtSite: { possible: boolean; raison: string | null; site: string; client: string | null };
}

/** Ce qui sera effacé, dans l'ordre de lecture d'un dossier. */
const LIBELLES_COMPTES: [cle: string, un: string, plusieurs: string][] = [
  ['pieces', 'pièce', 'pièces'], ['versions', 'version de fichier', 'versions de fichiers'], ['exigences', 'exigence', 'exigences'],
  ['ecarts', 'écart', 'écarts'], ['questions', 'question', 'questions'], ['devis', 'devis', 'devis'],
  ['revisions', 'révision de devis', 'révisions de devis'], ['devisRecus', 'devis reçu', 'devis reçus'], ['courriers', 'courrier', 'courriers'],
  ['memoires', 'mémoire technique', 'mémoires techniques'], ['dimensionnementsCvc', 'dimensionnement CVC', 'dimensionnements CVC'],
  ['schemas', 'schéma', 'schémas'], ['plans', 'plan', 'plans'], ['cheminements', 'cheminement', 'cheminements'],
  ['fichiers', 'fichier stocké', 'fichiers stockés'],
];

export const MENTION_SAUVEGARDES = 'Les sauvegardes du serveur conservent ces données jusqu’à leur rotation automatique. '
  + 'Les textes déjà envoyés à l’assistance IA ne sont pas conservés par TRAME ; chez le fournisseur IA, ils suivent ses conditions de conservation.';

function useRouvrir(dossierId: string) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [enCours, setEnCours] = useState(false);
  const rouvrir = async () => {
    setEnCours(true);
    try {
      await api.post(`/api/dossiers/${dossierId}/rouvrir`);
      await qc.invalidateQueries();
      notifier('Dossier rouvert : il est de nouveau modifiable.');
    } catch (e) { notifier(messageErreur(e), true); } finally { setEnCours(false); }
  };
  return { rouvrir, enCours };
}

/** Bandeau d'un dossier classé sans suite : motif, date, auteur, et réouverture. */
export function BandeauClasse({ dossierId, classement }: { dossierId: string; classement: ClassementDossier }) {
  const { peut } = useSession();
  const { rouvrir, enCours } = useRouvrir(dossierId);
  // Court : sur téléphone, les étapes du dossier doivent rester visibles sous le bandeau.
  return (
    <div className="fd-bandeau">
      <Alerte ton="ambre" titre="Classé sans suite — lecture seule"
        action={peut('dossier.ecrire') ? <Bouton icone={<ArchiveRestore aria-hidden />} chargement={enCours} onClick={rouvrir}>Rouvrir</Bouton> : undefined}>
        {LIBELLES_MOTIF[classement.motif] ?? 'Autre'} · le {date(classement.le)}{classement.par ? ` par ${classement.par}` : ''}
        {classement.commentaire && <span className="fd-commentaire" title={classement.commentaire}> — « {classement.commentaire} »</span>}
      </Alerte>
    </div>
  );
}

/** Menu « Plus » de l'en-tête du dossier. Absent si l'utilisateur n'a aucune de ces actions. */
export function MenuDossier({ dossier }: { dossier: DossierMin }) {
  const { peut } = useSession();
  const [ouvert, setOuvert] = useState(false);
  const [dialogue, setDialogue] = useState<'classer' | 'effacer' | null>(null);
  const { rouvrir } = useRouvrir(dossier.id);
  const racine = useRef<HTMLDivElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const idMenu = useId();
  const classe = !!dossier.classement;
  const ecrire = peut('dossier.ecrire');
  const effacer = peut('dossier.effacer');

  // Position du menu : sous le bouton, toujours entièrement dans l'écran (16 px de marge), quelle que soit la place du bouton.
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const placer = () => {
    const r = bouton.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(320, window.innerWidth - 32);
    setPos({ top: r.bottom + 6, width, left: Math.max(16, Math.min(r.right - width, window.innerWidth - width - 16)) });
  };

  useEffect(() => {
    if (!ouvert) return;
    racine.current?.querySelector<HTMLElement>('[role=menuitem]')?.focus({ preventScroll: true });
    const fermerAuDefilement = () => setOuvert(false);
    window.addEventListener('resize', placer);
    window.addEventListener('scroll', fermerAuDefilement, { passive: true });
    const dehors = (e: MouseEvent) => { if (!racine.current?.contains(e.target as Node)) setOuvert(false); };
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOuvert(false); bouton.current?.focus(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = [...(racine.current?.querySelectorAll<HTMLElement>('[role=menuitem]') ?? [])];
      const i = items.indexOf(document.activeElement as HTMLElement);
      e.preventDefault();
      items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
    };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', touche);
    return () => {
      document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', touche);
      window.removeEventListener('resize', placer); window.removeEventListener('scroll', fermerAuDefilement);
    };
  }, [ouvert]);

  if (!ecrire && !effacer) return null;
  const choisir = (f: () => void) => () => { setOuvert(false); f(); };
  return (
    <div className="fd-menu" ref={racine}>
      <button type="button" className="btn" ref={bouton} aria-haspopup="menu" aria-expanded={ouvert} aria-controls={ouvert ? idMenu : undefined}
        onClick={() => { if (!ouvert) placer(); setOuvert(!ouvert); }}><MoreHorizontal aria-hidden />Plus</button>
      {ouvert && pos && (
        <div className="fd-menu-liste" role="menu" id={idMenu} aria-label="Autres actions du dossier" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          {ecrire && !classe && (
            <button type="button" role="menuitem" className="fd-menu-item" onClick={choisir(() => setDialogue('classer'))}>
              <Archive aria-hidden /><span><span className="fd-menu-titre">Classer sans suite</span>
                <span className="tres-petit discret">Abandon, marché perdu… Réversible.</span></span>
            </button>
          )}
          {ecrire && classe && (
            <button type="button" role="menuitem" className="fd-menu-item" onClick={choisir(rouvrir)}>
              <ArchiveRestore aria-hidden /><span><span className="fd-menu-titre">Rouvrir le dossier</span>
                <span className="tres-petit discret">Il redevient actif et modifiable.</span></span>
            </button>
          )}
          {effacer && (
            <div className="fd-zone-danger" role="group" aria-label="Zone sensible">
              <span className="tres-petit fd-zone-titre">Zone sensible</span>
              <button type="button" role="menuitem" className="fd-menu-item fd-menu-danger" onClick={choisir(() => setDialogue('effacer'))}>
                <Trash2 aria-hidden /><span><span className="fd-menu-titre">Effacer définitivement…</span>
                  <span className="tres-petit">Tout le dossier, sans retour possible.</span></span>
              </button>
            </div>
          )}
        </div>
      )}
      {dialogue === 'classer' && <DialogueClasser dossierId={dossier.id} fermer={() => setDialogue(null)} />}
      {dialogue === 'effacer' && <DialogueEffacer dossierId={dossier.id} fermer={() => setDialogue(null)} />}
    </div>
  );
}

function DialogueClasser({ dossierId, fermer }: { dossierId: string; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [motif, setMotif] = useState<MotifClassement | ''>('');
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const valider = async () => {
    if (!motif) { setErreur('Choisissez un motif.'); return; }
    setEnCours(true);
    try {
      await api.post(`/api/dossiers/${dossierId}/classer`, { motif, commentaire: commentaire.trim() || null });
      await qc.invalidateQueries();
      notifier('Dossier classé sans suite. Retrouvez-le dans le filtre « Classés sans suite ».');
      fermer();
    } catch (e) { notifier(messageErreur(e), true); } finally { setEnCours(false); }
  };
  return (
    <Dialogue titre="Classer le dossier sans suite" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton>
        <Bouton variante="principal" icone={<Archive aria-hidden />} chargement={enCours} onClick={valider}>Classer sans suite</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire">Le dossier quitte la liste des dossiers actifs et passe en lecture seule. Rien n’est effacé : vous pourrez le rouvrir à tout moment.</p>
        <ChampChoix libelle="Motif" value={motif} vide="Choisir un motif" erreur={erreur}
          onChange={(e) => { setMotif(e.target.value as MotifClassement); setErreur(null); }}
          options={MOTIFS_CLASSEMENT.map((m) => ({ valeur: m, libelle: LIBELLES_MOTIF[m] }))} />
        <ChampTexte libelle="Commentaire (facultatif)" rows={3} maxLength={COMMENTAIRE_MAX} value={commentaire}
          onChange={(e) => setCommentaire(e.target.value.slice(0, COMMENTAIRE_MAX))}
          aide={`${commentaire.length} / ${COMMENTAIRE_MAX} caractères — n’y mettez pas d’information personnelle.`} />
      </div>
    </Dialogue>
  );
}

function DialogueEffacer({ dossierId, fermer }: { dossierId: string; fermer: () => void }) {
  const qc = useQueryClient();
  const naviguer = useNavigate();
  const notifier = useNotifier();
  const idSaisie = useId();
  const [saisie, setSaisie] = useState('');
  const [clientEtSite, setClientEtSite] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const q = useQuery({ queryKey: ['effacement', dossierId], queryFn: () => api.get<Recapitulatif>(`/api/dossiers/${dossierId}/effacement`), gcTime: 0 });
  const r = q.data;
  const pret = !!r && !r.refus && confirmationValide(saisie, r);

  const effacer = async () => {
    if (!r || !pret) return;
    setEnCours(true);
    try {
      await api.post(`/api/dossiers/${dossierId}/effacement`, { confirmation: saisie, clientEtSite: clientEtSite && r.clientEtSite.possible });
      qc.removeQueries({ queryKey: ['dossier', dossierId] });
      naviguer('/etudes', { replace: true });
      await qc.invalidateQueries();
      notifier(`Dossier ${r.reference} effacé définitivement.`);
    } catch (e) { notifier(messageErreur(e), true); setEnCours(false); }
  };

  const comptes = r ? LIBELLES_COMPTES.filter(([k]) => (r.comptes[k] ?? 0) > 0) : [];
  return (
    <Dialogue titre="Effacer définitivement le dossier" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton>
        {r && !r.refus && <Bouton variante="danger" icone={<Trash2 aria-hidden />} chargement={enCours} disabled={!pret} onClick={effacer}>Effacer définitivement</Bouton>}</>}>
      {q.isLoading && <Chargement lignes={4} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {r && (
        <div className="pile">
          {r.refus ? <Alerte ton="danger" titre="Effacement impossible">{r.refus.message}</Alerte> : (
            <Alerte ton="danger" titre="Sans retour possible">Le dossier <span className="mono">{r.reference}</span> et tout ce qu’il contient seront effacés de TRAME.</Alerte>
          )}
          <div>
            <p className="petit" style={{ marginBottom: 6 }}><strong>Sera effacé :</strong></p>
            {comptes.length ? (
              <ul className="fd-comptes">{comptes.map(([k, un, plusieurs]) => <li key={k}>{pluriel(r.comptes[k], un, plusieurs)}</li>)}</ul>
            ) : <p className="petit secondaire">Le dossier seul (aucune pièce, aucun devis).</p>}
            <p className="tres-petit secondaire" style={{ marginTop: 6 }}>Le journal garde seulement qui a effacé, quand, et ces nombres — aucun contenu.</p>
          </div>
          <p className="tres-petit secondaire">{MENTION_SAUVEGARDES}</p>
          {!r.refus && (
            <>
              <label className="case">
                <input type="checkbox" checked={clientEtSite && r.clientEtSite.possible} disabled={!r.clientEtSite.possible}
                  onChange={(e) => setClientEtSite(e.target.checked)} aria-describedby={r.clientEtSite.raison ? `${idSaisie}-cs` : undefined} />
                <span className="petit">Effacer aussi {r.clientEtSite.client ? <>le client « {r.clientEtSite.client} » et </> : null}le site « {r.clientEtSite.site} »
                  {r.clientEtSite.raison && <span id={`${idSaisie}-cs`} className="tres-petit secondaire" style={{ display: 'block' }}>{r.clientEtSite.raison}</span>}
                </span>
              </label>
              <div className="champ">
                <label className="champ-libelle" htmlFor={idSaisie}>Pour confirmer, recopiez la référence <span className="mono">{r.reference}</span></label>
                <input id={idSaisie} className="saisie saisie-mono" value={saisie} maxLength={400} autoComplete="off" spellCheck={false}
                  onChange={(e) => setSaisie(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') effacer(); }} />
              </div>
            </>
          )}
        </div>
      )}
    </Dialogue>
  );
}
